import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ChefHat, Clock, Users, Leaf, Check, Printer, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const RecipeDetail = () => {
  const { recipeId } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkedSteps, setCheckedSteps] = useState([]);

  const parseRecipe = useCallback((text, index) => {
    try {
      const recipeMatch = text.match(/RECIPE_START([\s\S]*?)RECIPE_END/);
      if (recipeMatch) {
        const recipeText = recipeMatch[1].trim();
        const recipeBlocks = recipeText.split(/Recipe \d+:/).filter(r => r.trim());
        
        if (recipeBlocks[index]) {
          const block = recipeBlocks[index];
          const lines = block.split('\n').filter(l => l.trim());
          
          // Extract recipe name (first line)
          const name = lines[0]?.trim() || '';
          
          // Extract ingredients (everything after "Ingredients:" until "Instructions:")
          const ingredientsStart = block.indexOf('Ingredients:');
          const instructionsStart = block.indexOf('Instructions:');
          const ingredientsText = ingredientsStart !== -1 && instructionsStart !== -1
            ? block.substring(ingredientsStart + 'Ingredients:'.length, instructionsStart).trim()
            : '';
          
          // Extract instructions (everything after "Instructions:" until "Benefits:")
          const benefitsStart = block.indexOf('Benefits:');
          const instructionsText = instructionsStart !== -1 && benefitsStart !== -1
            ? block.substring(instructionsStart + 'Instructions:'.length, benefitsStart).trim()
            : '';
          
          // Extract benefits (everything after "Benefits:")
          const benefitsText = benefitsStart !== -1
            ? block.substring(benefitsStart + 'Benefits:'.length).trim()
            : '';
          
          const parsedRecipe = {
            name: name,
            ingredients: ingredientsText,
            instructions: instructionsText,
            benefits: benefitsText
          };
          
          setRecipe(parsedRecipe);
        } else {
          toast.error("Recipe not found");
          navigate('/recommendations');
        }
      }
    } catch (error) {
      console.error('Error parsing recipe:', error);
      toast.error("Error loading recipe");
      navigate('/recommendations');
    }
    
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    const storedData = sessionStorage.getItem('recommendations');
    if (!storedData) {
      toast.error("No recommendations found. Please complete the questionnaire first.");
      navigate('/questionnaire');
      return;
    }

    const recData = JSON.parse(storedData);
    parseRecipe(recData.recommendations, parseInt(recipeId));
  }, [recipeId, navigate, parseRecipe]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-emerald-50 to-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading recipe...</p>
        </div>
      </div>
    );
  }

  if (!recipe) {
    return null;
  }

  const toggleStep = (index) => {
    if (checkedSteps.includes(index)) {
      setCheckedSteps(checkedSteps.filter(i => i !== index));
    } else {
      setCheckedSteps([...checkedSteps, index]);
    }
  };

  const handlePrint = () => {
    window.print();
    toast.success("Print dialog opened");
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: recipe.name,
        text: `Check out this herbal recipe: ${recipe.name}`,
      }).then(() => toast.success("Shared successfully!"))
        .catch(() => toast.error("Failed to share"));
    } else {
      toast.success("Link copied to clipboard!");
    }
  };

  // Parse ingredients and instructions
  // Split ingredients by newline or dash/bullet
  const ingredientsList = recipe.ingredients
    .split(/\n|[-•]/)
    .map(i => i.trim())
    .filter(Boolean)
    .filter(i => i.length > 2); // Filter out very short lines
  
  // Split instructions by numbered format (1. 2. 3. etc)
  const instructionsList = recipe.instructions
    .split(/\d+\.\s+/)
    .map(i => i.trim())
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Link
          to="/recommendations"
          className="inline-flex items-center text-primary hover:text-primary/80 mb-8 font-medium"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Recommendations
        </Link>

        {/* Recipe Header */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-primary to-emerald-700 text-white p-8">
            <div className="flex items-center mb-4">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mr-4">
                <ChefHat className="w-8 h-8" />
              </div>
              <div>
                <h1 className="font-playfair text-3xl md:text-4xl font-bold">{recipe.name}</h1>
                <p className="text-emerald-100 mt-1">Herbal Wellness Recipe</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-6 mt-6">
              <div className="flex items-center">
                <Clock className="w-5 h-5 mr-2" />
                <div>
                  <p className="text-xs text-emerald-200">Prep Time</p>
                  <p className="font-semibold">10-15 min</p>
                </div>
              </div>
              <div className="flex items-center">
                <Users className="w-5 h-5 mr-2" />
                <div>
                  <p className="text-xs text-emerald-200">Servings</p>
                  <p className="font-semibold">1-2 people</p>
                </div>
              </div>
              <div className="flex items-center">
                <Leaf className="w-5 h-5 mr-2" />
                <div>
                  <p className="text-xs text-emerald-200">Type</p>
                  <p className="font-semibold">Herbal</p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 p-6 border-b border-gray-100">
            <Button onClick={handlePrint} variant="outline" className="flex-1 rounded-full">
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            <Button onClick={handleShare} variant="outline" className="flex-1 rounded-full">
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>

          {/* Benefits */}
          <div className="p-6 bg-emerald-50 border-b border-gray-100">
            <h3 className="font-playfair text-xl font-bold text-gray-900 mb-3 flex items-center">
              <Leaf className="w-5 h-5 text-primary mr-2" />
              Health Benefits
            </h3>
            <p className="text-gray-700 leading-relaxed">{recipe.benefits}</p>
          </div>

          {/* Ingredients */}
          <div className="p-6 border-b border-gray-100">
            <h3 className="font-playfair text-2xl font-bold text-gray-900 mb-4">Ingredients</h3>
            <div className="grid md:grid-cols-2 gap-3">
              {ingredientsList.map((ingredient, index) => (
                <div key={`${ingredient}-${index}`} className="flex items-center bg-gray-50 rounded-lg p-3">
                  <div className="w-2 h-2 bg-primary rounded-full mr-3 flex-shrink-0"></div>
                  <span className="text-gray-700">{ingredient}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Instructions */}
          <div className="p-6">
            <h3 className="font-playfair text-2xl font-bold text-gray-900 mb-4">Instructions</h3>
            <div className="space-y-4">
              {instructionsList.map((instruction, index) => (
                <div
                  key={`${instruction.slice(0, 24)}-${index}`}
                  className={`flex items-start gap-4 p-4 rounded-lg border-2 transition-all cursor-pointer ${
                    checkedSteps.includes(index)
                      ? 'border-primary bg-secondary'
                      : 'border-gray-200 hover:border-primary/50'
                  }`}
                  onClick={() => toggleStep(index)}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1 ${
                    checkedSteps.includes(index)
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {checkedSteps.includes(index) ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <span className="font-bold">{index + 1}</span>
                    )}
                  </div>
                  <p className={`text-gray-700 leading-relaxed flex-1 ${
                    checkedSteps.includes(index) ? 'line-through opacity-60' : ''
                  }`}>
                    {instruction}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Safety Note */}
        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-6">
          <h4 className="font-semibold text-amber-900 mb-2 flex items-center">
            <Leaf className="w-5 h-5 mr-2" />
            Important Safety Note
          </h4>
          <p className="text-sm text-amber-800 leading-relaxed">
            This recipe is for educational purposes. Always consult your healthcare provider before using herbal remedies, especially if you are pregnant, nursing, taking medications, or have a medical condition.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RecipeDetail;
